// Components/StatisticsPanel.js
import React from 'react';
import {Activity, BarChart, Clock, FileText, Loader, PieChart, UserX} from 'lucide-react';

const StatisticsPanel = ({statistics, loading}) => {
    // Helper to format numbers with commas
    const formatNumber = (num) => {
        if (num === undefined || num === null) return '0';
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    // Stats cards
    const renderStatsCard = (title, value, icon, color) => (
        <div className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
            <div className="flex items-center justify-between mb-3">
                <div className={`w-12 h-12 ${color} rounded-lg flex items-center justify-center`}>
                    {icon}
                </div>
            </div>
            <p className="text-sm text-gray-600 font-medium">{title}</p>
            <p className="text-2xl font-bold text-gray-800 mt-1">{formatNumber(value)}</p>
        </div>
    );

    // Loading state
    if (loading) {
        return (
            <div className="py-8 text-center">
                <Loader size={32} className="mx-auto text-orange-500 animate-spin mb-4"/>
                <p className="text-gray-600">Loading statistics...</p>
            </div>
        );
    }

    // Empty state
    if (!statistics) {
        return (
            <div className="text-center py-8">
                <Activity size={48} className="mx-auto text-gray-300 mb-4"/>
                <h3 className="text-lg font-medium text-gray-600 mb-2">No statistics available</h3>
                <p className="text-gray-500">
                    Try selecting a different time range or school.
                </p>
            </div>
        );
    }

    // Calculate activity breakdown percentages for the donut chart
    const eventTypes = statistics.event_types || {};
    const totalEvents = Object.values(eventTypes).reduce((sum, count) => sum + count, 0) || 1; // Avoid division by zero

    // Get event percentages
    const eventPercentages = {};
    Object.entries(eventTypes).forEach(([type, count]) => {
        eventPercentages[type] = Math.round((count / totalEvents) * 100);
    });

    return (
        <div className="space-y-8">
            {/* Top Stats Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {renderStatsCard(
                    "Total Events",
                    statistics.total_events || 0,
                    <Activity size={24} className="text-blue-600"/>,
                    "bg-blue-50"
                )}
                {renderStatsCard(
                    "Failed Logins",
                    statistics.failed_logins || 0,
                    <UserX size={24} className="text-red-600"/>,
                    "bg-red-50"
                )}
                {renderStatsCard(
                    "Audit Logs",
                    statistics.audit_logs_count || 0,
                    <FileText size={24} className="text-purple-600"/>,
                    "bg-purple-50"
                )}
                {renderStatsCard(
                    "Security Events",
                    statistics.security_events_count || 0,
                    <Activity size={24} className="text-orange-600"/>,
                    "bg-orange-50"
                )}
            </div>

            {/* Activity Charts */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Activity Over Time */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-800">Activity Over Time</h3>
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                            <Clock size={16}/>
                            Last 30 days
                        </div>
                    </div>

                    {/* Simulated bar chart since we don't have actual chart library */}
                    <div className="h-64 flex items-end justify-between space-x-2">
                        {Array.from({length: 10}, (_, i) => {
                            const height = Math.max(20, Math.random() * 100); // Random heights for demo
                            return (
                                <div key={i} className="flex-1 flex flex-col items-center">
                                    <div
                                        className="w-full bg-blue-500 rounded-t"
                                        style={{height: `${height}%`}}
                                    ></div>
                                    <div className="text-xs text-gray-500 mt-2">{i + 1}</div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="mt-4 text-center text-sm text-gray-500">
                        <BarChart size={16} className="inline mr-1"/>
                        Showing sample activity pattern (demo data)
                    </div>
                </div>

                {/* Event Type Breakdown */}
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-800">Event Type Breakdown</h3>
                        <PieChart size={18} className="text-gray-500"/>
                    </div>

                    {/* Event type breakdown */}
                    <div className="space-y-4">
                        {Object.entries(eventPercentages).map(([type, percentage]) => (
                            <div key={type}>
                                <div className="flex justify-between mb-1">
                                    <span className="text-sm font-medium text-gray-700">{type}</span>
                                    <span className="text-sm text-gray-500">{percentage}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2.5">
                                    <div
                                        className="bg-blue-600 h-2.5 rounded-full"
                                        style={{width: `${percentage}%`}}
                                    ></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Additional Statistics */}
            {statistics.additional_metrics && (
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Additional Metrics</h3>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {Object.entries(statistics.additional_metrics).map(([key, value]) => (
                            <div key={key} className="p-4 bg-gray-50 rounded-lg">
                                <p className="text-sm text-gray-600 mb-1 capitalize">{key.replace(/_/g, ' ')}</p>
                                <p className="text-lg font-bold text-gray-800">{formatNumber(value)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default StatisticsPanel;